import { Path, PathValue } from 'react-hook-form';
import { IntlShape } from 'react-intl';

import { Choice } from '@components/fields/StackedButtonLikeInputs';
import { UniversalSearchSortOptionEnum } from '@typing/Enums';
import {
  AutomationActionBlockKindEnum,
  AutomationConditionKindEnum,
  AutomationTriggerBlockFrequencyEnum,
  AutomationTriggerBlockKindEnum,
  AutomationUserTypeEnum,
  BillableCalculationEnum,
  CaregivingDurationEnum,
  CaregivingImpactEnum,
  CaregivingTimePerWeekEnum,
  CareProfileActionableFieldEnum,
  CareProfileAcuityEnum,
  CareProfileAgeEnum,
  CareProfileCaregiverTypeEnum,
  CareProfileCarePhaseEnum,
  CareProfileCognitiveStatusEnum,
  CareProfileConservatorshipStatusEnum,
  CareProfileCurrentCareTransitionEnum,
  CareProfileDevelopmentalOrEducationalConcernEnum,
  CareProfileEmotionalConcernEnum,
  CareProfileEmploymentStatusEnum,
  CareProfileFallCountEnum,
  CareProfileFinancialManagerEnum,
  CareProfileGuardianTypeEnum,
  CareProfileGuidanceTypeEnum,
  CareProfileHealthInsuranceTypeEnum,
  CareProfileImmigrationStatusEnum,
  CareProfileIncomeSourceEnum,
  CareProfileInsuranceTypeEnum,
  CareProfileMaritalStatusEnum,
  CareProfileMedicationManagementStatusEnum,
  CareProfileProviderFinancialConcernEnum,
  CareProfileReligionEnum,
  CareProfileResidenceTypeEnum,
  CareProfileSenseOfFulfillmentEnum,
  CareProfileSocialSatisfactionEnum,
  CareProfileStabilitySupportEnum,
  CareProfileStageLimitedEnum,
  CareProfileTypesOfSupportNeededEnum,
  CareProfileUrgencyEnum,
  CareProfileUseCaseEnum,
  CareTypeEnum,
  ClientRoleProviderEmotionalStateEnum,
  ClientRoleProviderFeelsSupportedEnum,
  ClientRoleRelationshipEnum,
  ConsultDurationEnum,
  ConsultTopicEnum,
  ContactMethodEnum,
  DiagnosisControlEnum,
  DiagnosisSeverityEnum,
  DistanceToLovedOneEnum,
  DurableMedicalEquipmentEnum,
  DurationUnitsEnum,
  FocusAreaEnum,
  HouseholdIncomeLevelEnum,
  JourneysSearchDateRangeTypeEnum,
  LegalDocumentKindEnum,
  LegalDocumentStatusEnum,
  MedicationFormatEnum,
  MedicationKindEnum,
  MedicationPaymentTypeEnum,
  PronounsEnum,
  PushNotificationActionEnum,
  SeverityEnum,
  SignedDocumentKindEnum,
  SpecialtyAgeEnum,
  StageEnum,
  TaskClientVisibleEnum,
  TaskKindEnum,
  TrackerTypeEnum,
  YesNoDontKnowEnum
} from '@typing/Generated';
import { ImageName } from '@utils/imageUtils';
import {
  legalDocumentStatusIcon,
  providerEmotionalStateIcon,
  providerFeelsSupportedIcon,
  senseOfFulfillmentIcon,
  socialSatisfactionIcon
} from '@utils/modelUtils';
import { snakeToCamel } from '@utils/stringUtils';

const translateOptions = <T extends unknown>(options: T[], intl: IntlShape, prefix: string, suffix?: string) =>
  options.map(option => ({
    label: intl.formatMessage({
      id: `${prefix}.${option}${suffix ? `.${suffix}` : ''}`
    }),
    value: option
  }));

export const optionsForAcuity = (intl: IntlShape) => {
  const options = [
    CareProfileAcuityEnum.CHRONIC,
    CareProfileAcuityEnum.COMPLEX,
    CareProfileAcuityEnum.DAILY_NEEDS,
    CareProfileAcuityEnum.EPISODIC
  ];
  return translateOptions(options, intl, 'models.careProfile.acuityOptions');
};

export const optionsForAge = (intl: IntlShape, skipDeceased: boolean = false) => {
  let options = [
    CareProfileAgeEnum.sixty_five_and_older,
    CareProfileAgeEnum.twenty_five_to_sixty_four,
    CareProfileAgeEnum.eighteen_to_twenty_four,
    CareProfileAgeEnum.fifteen_to_seventeen,
    CareProfileAgeEnum.twelve_to_fourteen,
    CareProfileAgeEnum.six_to_eleven,
    CareProfileAgeEnum.four_to_five,
    CareProfileAgeEnum.one_to_three,
    CareProfileAgeEnum.under_one,
    CareProfileAgeEnum.deceased
  ];
  if (skipDeceased) {
    options = options.filter(option => option !== CareProfileAgeEnum.deceased);
  }
  return translateOptions(options, intl, 'models.careProfile.ageOptions');
};

export const optionsForAutomationActionBlockKinds = (intl: IntlShape) => {
  const options = [
    AutomationActionBlockKindEnum.ADD_TAG,
    AutomationActionBlockKindEnum.APPLY_CARE_PLAN_TASK_TEMPLATE,
    AutomationActionBlockKindEnum.APPLY_GOAL_TEMPLATE,
    AutomationActionBlockKindEnum.ARCHIVE_TAG,
    AutomationActionBlockKindEnum.COMPLETE_ALL_TASKS_FROM_TEMPLATE,
    AutomationActionBlockKindEnum.COMPLETE_ALL_TASKS_OF_KIND,
    AutomationActionBlockKindEnum.CREATE_MESSAGE,
    AutomationActionBlockKindEnum.CREATE_PUSH_NOTIFICATION,
    AutomationActionBlockKindEnum.CREATE_TASK,
    AutomationActionBlockKindEnum.NO_OP,
    AutomationActionBlockKindEnum.RECOMMEND_AN_ARTICLE,
    AutomationActionBlockKindEnum.REPLACE_TAG,
    AutomationActionBlockKindEnum.RECOMMEND_A_WORKSHEET_TEMPLATE
  ];

  return translateOptions(options, intl, 'models.automationActionBlock.kindOptions');
};

export const optionsForAutomationConditionKind = (intl: IntlShape) => {
  const options = [
    AutomationConditionKindEnum.CARE_PLAN_IS_PUBLISHED,
    AutomationConditionKindEnum.CARE_PROFILE_VALUE_IS,
    AutomationConditionKindEnum.FUTURE_CONSULT_SCHEDULED,
    AutomationConditionKindEnum.JOURNEY_IS_ACTIVE,
    AutomationConditionKindEnum.JOURNEY_REQUESTED_LANGUAGE_INTERPRETATION,
    AutomationConditionKindEnum.OWNER_EMPLOYER_IS,
    AutomationConditionKindEnum.MEMBER_MESSAGE_SINCE,
    AutomationConditionKindEnum.NEVER,
    AutomationConditionKindEnum.TIME_SINCE_LAST_CONSULT
  ];
  return translateOptions(options, intl, 'models.automationCondition.kinds');
};

export const optionsForAutomationTriggerBlockFrequency = (
  intl: IntlShape,
  availableFrequencies: AutomationTriggerBlockFrequencyEnum[]
) => {
  const options = [AutomationTriggerBlockFrequencyEnum.EVERY_TIME, AutomationTriggerBlockFrequencyEnum.FIRST_TIME_ONLY];

  return translateOptions(
    options.filter(o => availableFrequencies.includes(o)),
    intl,
    'models.automationTriggerBlock.frequencyOptions'
  );
};

export const optionsForAutomationTriggerBlockKinds = (intl: IntlShape) => {
  const options = [
    AutomationTriggerBlockKindEnum.ARTICLE_OPENED,
    AutomationTriggerBlockKindEnum.CARE_PLAN_ACTION_ITEM_COMPLETED,
    AutomationTriggerBlockKindEnum.CARE_PLAN_PUBLISHED,
    AutomationTriggerBlockKindEnum.CARE_PROFILE_UPDATED,
    AutomationTriggerBlockKindEnum.CARE_TEAM_INVITE_ACCEPTED,
    AutomationTriggerBlockKindEnum.CARE_TEAM_INVITE_SENT,
    AutomationTriggerBlockKindEnum.CLIENT_LOCATION_CHANGED,
    AutomationTriggerBlockKindEnum.CLIENT_RELATIONSHIP_CHANGED,
    AutomationTriggerBlockKindEnum.CONSULT_CANCELED,
    AutomationTriggerBlockKindEnum.CONSULT_SCHEDULED,
    AutomationTriggerBlockKindEnum.EMPLOYER_BENEFIT_OPENED,
    AutomationTriggerBlockKindEnum.FILE_RESOURCE_ADDED,
    AutomationTriggerBlockKindEnum.FREE_TEXT_KEYWORD_MATCHED,
    AutomationTriggerBlockKindEnum.GOAL_ADDED,
    AutomationTriggerBlockKindEnum.INITIAL_EXPERT_ASSIGNED,
    AutomationTriggerBlockKindEnum.JOURNEY_ADDED,
    AutomationTriggerBlockKindEnum.JOURNEY_TAG_ADDED,
    AutomationTriggerBlockKindEnum.LOSES_EXPERT_ACCESS_UPCOMING,
    AutomationTriggerBlockKindEnum.MESSAGE_SENT,
    AutomationTriggerBlockKindEnum.NEVER,
    AutomationTriggerBlockKindEnum.PACKAGE_GRANT_PURCHASED,
    AutomationTriggerBlockKindEnum.PACKAGE_GRANT_EXPIRATION_UPCOMING,
    AutomationTriggerBlockKindEnum.PACKAGE_GRANT_ADDED,
    AutomationTriggerBlockKindEnum.RECOMMENDED_NEXT_CONSULT_UPCOMING,
    AutomationTriggerBlockKindEnum.RESOURCE_ADDED,
    AutomationTriggerBlockKindEnum.RESOURCE_MARKED_NOT_USEFUL,
    AutomationTriggerBlockKindEnum.RESOURCE_MARKED_USEFUL,
    AutomationTriggerBlockKindEnum.RESOURCE_PINNED,
    AutomationTriggerBlockKindEnum.RESOURCE_UNPINNED,
    AutomationTriggerBlockKindEnum.SIGNED_DOCUMENT_COMPLETED,
    AutomationTriggerBlockKindEnum.TASK_ADDED,
    AutomationTriggerBlockKindEnum.TASK_COMPLETED,
    AutomationTriggerBlockKindEnum.TIME_LOG_ADDED,
    AutomationTriggerBlockKindEnum.TIME_SINCE_LAST_CONSULT,
    AutomationTriggerBlockKindEnum.TRACKER_COMPLETED,
    AutomationTriggerBlockKindEnum.TRACKER_STARTED,
    AutomationTriggerBlockKindEnum.WORKSHEET_COMPLETED,
    AutomationTriggerBlockKindEnum.WORKSHEET_INCOMPLETED,
    AutomationTriggerBlockKindEnum.WORKSHEET_STARTED
  ];

  return translateOptions(options, intl, 'models.automationTriggerBlock.kindOptions');
};

export const optionsForAutomationUserType = (
  intl: IntlShape,
  availableTypes: AutomationUserTypeEnum[] = Object.values(AutomationUserTypeEnum)
) => {
  const options = [
    AutomationUserTypeEnum.ANYONE,
    AutomationUserTypeEnum.CLIENT_ANY,
    AutomationUserTypeEnum.CLIENT_OWNER,
    AutomationUserTypeEnum.EXPERT_ANY,
    AutomationUserTypeEnum.EXPERT_PRIMARY
  ];

  return translateOptions(
    options.filter(o => availableTypes.includes(o)),
    intl,
    'models.automationTriggerBlock.userTypeOptions'
  );
};

export const optionsForBillableCalculation = (intl: IntlShape) =>
  translateOptions(Object.values(BillableCalculationEnum), intl, 'models.employerContract.billableCalculation');

export const optionsForBoolean = (intl: IntlShape) => [
  { label: intl.formatMessage({ id: 'dictionary.yes' }), value: true },
  { label: intl.formatMessage({ id: 'dictionary.no' }), value: false }
];

export const optionsForCaregivingDuration = (intl: IntlShape) => {
  const options = [
    CaregivingDurationEnum.LESS_THAN_ONE_YEAR,
    CaregivingDurationEnum.ONE_TO_TWO_YEARS,
    CaregivingDurationEnum.TWO_TO_THREE_YEARS,
    CaregivingDurationEnum.FOUR_TO_FIVE_YEARS,
    CaregivingDurationEnum.SIX_OR_MORE_YEARS
  ];
  return translateOptions(options, intl, 'models.careProfile.caregivingDurationOptions');
};

export const optionsForCaregivingImpact = (intl: IntlShape) => {
  const options = [
    CaregivingImpactEnum.HIGH,
    CaregivingImpactEnum.MEDIUM,
    CaregivingImpactEnum.LOW,
    CaregivingImpactEnum.NONE,
    CaregivingImpactEnum.UNKNOWN
  ];
  return translateOptions(options, intl, 'models.careProfile.caregivingImpactOptions');
};

export const optionsForCaregivingTimePerWeek = (intl: IntlShape) => {
  const options = [
    CaregivingTimePerWeekEnum.LESS_THAN_TWO_HOURS,
    CaregivingTimePerWeekEnum.TWO_TO_FOUR_HOURS,
    CaregivingTimePerWeekEnum.FIVE_TO_TEN_HOURS,
    CaregivingTimePerWeekEnum.ELEVEN_TO_TWENTY_HOURS,
    CaregivingTimePerWeekEnum.TWENTY_TO_THIRTY_HOURS,
    CaregivingTimePerWeekEnum.IT_DEPENDS
  ];
  return translateOptions(options, intl, 'models.careProfile.caregivingTimePerWeekOptions');
};

export const optionsForCaregivingTimePerWeekGrieving = (intl: IntlShape) => {
  const options = [
    CaregivingTimePerWeekEnum.LESS_THAN_ONE_HOUR,
    CaregivingTimePerWeekEnum.ONE_TO_TWO_HOURS,
    CaregivingTimePerWeekEnum.TWO_TO_FOUR_HOURS,
    CaregivingTimePerWeekEnum.FOUR_OR_MORE_HOURS
  ];
  return translateOptions(options, intl, 'models.careProfile.caregivingTimePerWeekOptions');
};

export const optionsForCareProfileActionableField = (intl: IntlShape) => {
  const options = [
    CareProfileActionableFieldEnum.age,
    CareProfileActionableFieldEnum.caregiver_types,
    CareProfileActionableFieldEnum.cognitive_statuses,
    CareProfileActionableFieldEnum.current_care_transitions,
    CareProfileActionableFieldEnum.emotional_concerns,
    CareProfileActionableFieldEnum.fall_count,
    CareProfileActionableFieldEnum.financial_manager,
    CareProfileActionableFieldEnum.guidance_type,
    CareProfileActionableFieldEnum.health_insurance_types,
    CareProfileActionableFieldEnum.insurance_types,
    CareProfileActionableFieldEnum.legal_help_needed,
    CareProfileActionableFieldEnum.marital_status,
    CareProfileActionableFieldEnum.medications_affordable,
    CareProfileActionableFieldEnum.provider_financial_concerns,
    CareProfileActionableFieldEnum.recent_hospitalization,
    CareProfileActionableFieldEnum.religion,
    CareProfileActionableFieldEnum.residence_type,
    CareProfileActionableFieldEnum.sense_of_fulfillment,
    CareProfileActionableFieldEnum.social_satisfaction,
    CareProfileActionableFieldEnum.stability_supports,
    CareProfileActionableFieldEnum.types_of_support_needed,
    CareProfileActionableFieldEnum.urgency
  ];

  return options.map(option => ({
    label: intl.formatMessage({ id: `models.careProfile.${snakeToCamel(option)}` }),
    value: option
  }));
};

export const optionsForCareTypes = (intl: IntlShape) =>
  translateOptions(Object.values(CareTypeEnum), intl, 'models.careProfile.careTypesProvidedOptions');

export const optionsForCaregiverType = (intl: IntlShape) => {
  const options = [
    CareProfileCaregiverTypeEnum.children,
    CareProfileCaregiverTypeEnum.spouse,
    CareProfileCaregiverTypeEnum.siblings,
    CareProfileCaregiverTypeEnum.parents,
    CareProfileCaregiverTypeEnum.step_parents,
    CareProfileCaregiverTypeEnum.grandparents,
    CareProfileCaregiverTypeEnum.relatives,
    CareProfileCaregiverTypeEnum.friends,
    CareProfileCaregiverTypeEnum.caregivers,
    CareProfileCaregiverTypeEnum.neighbors,
    CareProfileCaregiverTypeEnum.other
  ];
  return translateOptions(options, intl, 'models.careProfile.caregiverTypesOptions');
};

export const optionsForCognitiveStatus = (intl: IntlShape) => {
  const options = [
    CareProfileCognitiveStatusEnum.none,
    CareProfileCognitiveStatusEnum.forgetful,
    CareProfileCognitiveStatusEnum.short_term_memory_loss,
    CareProfileCognitiveStatusEnum.long_term_memory_loss
  ];

  return translateOptions(options, intl, 'models.careProfile.cognitiveStatusesOptions');
};

export const optionsForConservatorshipStatus = (intl: IntlShape) => {
  const options = [
    CareProfileConservatorshipStatusEnum.CONSERVED,
    CareProfileConservatorshipStatusEnum.NOT_CONSERVED,
    CareProfileConservatorshipStatusEnum.NOT_ADDRESSED
  ];

  return translateOptions(options, intl, 'models.careProfile.conservatorshipStatusOptions');
};

export type ConsultTopicCategory = 'guide' | 'research' | 'saveTime';

export const consultTopicOrder: { [key in ConsultTopicCategory]: string[] } = {
  guide: [
    ConsultTopicEnum.HANDLE_REFERRALS,
    ConsultTopicEnum.MEDIATE_FAMILY_MEETING,
    ConsultTopicEnum.ARRANGE_AUTISM_EVALUATION,
    ConsultTopicEnum.RELOCATION_GUIDANCE,
    ConsultTopicEnum.ASSISTED_LIVING_SUPPORT,
    ConsultTopicEnum.ARRANGE_MEDICAL_SECOND_OPINION,
    ConsultTopicEnum.ARRANGE_MEMORY_LOSS_EVALUATION,
    ConsultTopicEnum.COMPARE_CARE_COSTS
  ],
  research: [
    ConsultTopicEnum.HOUSING_OPTIONS,
    ConsultTopicEnum.FIND_LAWYER,
    ConsultTopicEnum.FIND_DOCTOR,
    ConsultTopicEnum.FIND_RESPITE_OPTIONS,
    ConsultTopicEnum.FIND_SUPPORT_GROUPS,
    ConsultTopicEnum.FIND_IN_HOME_AIDE,
    ConsultTopicEnum.FIND_FINANCIAL_PLANNER,
    ConsultTopicEnum.MENTAL_HEALTH_RESOURCES
  ],
  saveTime: [
    ConsultTopicEnum.SCHEDULE_APPOINTMENT,
    ConsultTopicEnum.INTERVIEW_POTENTIAL_CAREGIVERS,
    ConsultTopicEnum.ARRANGE_RIDE,
    ConsultTopicEnum.SUBMIT_INSURANCE_CLAIMS,
    ConsultTopicEnum.ARRANGE_HOME_SAFETY_EVALUATION,
    ConsultTopicEnum.COMPLETE_APPLICATIONS,
    ConsultTopicEnum.COORDINATE_EMERGENCY_RESPONSE
  ]
};

export const optionsForConsultTopics = (category: ConsultTopicCategory, intl: IntlShape) =>
  translateOptions(consultTopicOrder[category], intl, 'models.consult.topics.options');

export const optionsForCurrentCareTransition = (intl: IntlShape) => {
  const options = [
    { option: CareProfileCurrentCareTransitionEnum.none },
    { option: CareProfileCurrentCareTransitionEnum.adding_part_time_care },
    { option: CareProfileCurrentCareTransitionEnum.adding_full_time_care },
    { option: CareProfileCurrentCareTransitionEnum.reducing_care },
    { option: CareProfileCurrentCareTransitionEnum.moving },
    { option: CareProfileCurrentCareTransitionEnum.transferring },
    { option: CareProfileCurrentCareTransitionEnum.going_home },
    { option: CareProfileCurrentCareTransitionEnum.other }
  ];
  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.careProfile.currentCareTransitionsOptions.${option.option}`
    }),
    value: option.option
  }));
};

export const optionsForDevelopmentalOrEducationalConcerns = (intl: IntlShape) =>
  translateOptions(
    Object.values(CareProfileDevelopmentalOrEducationalConcernEnum),
    intl,
    'models.careProfile.developmentalOrEducationalConcernsOptions'
  );

export const optionsForDiagnosisControl = (intl: IntlShape) => {
  const options = [
    DiagnosisControlEnum.CONTROLLED,
    DiagnosisControlEnum.UNCONTROLLED,
    DiagnosisControlEnum.UNKNOWN,
    DiagnosisControlEnum.NA
  ];
  return translateOptions(options, intl, 'models.diagnosis.controlOptions');
};

export const optionsForDiagnosisSeverity = (intl: IntlShape) => {
  const options = [
    DiagnosisSeverityEnum.MILD,
    DiagnosisSeverityEnum.MODERATE,
    DiagnosisSeverityEnum.SEVERE,
    DiagnosisSeverityEnum.UNKNOWN,
    DiagnosisSeverityEnum.NA
  ];

  return translateOptions(options, intl, 'models.diagnosis.severityOptions');
};

export const optionsForDistanceToLovedOne = (intl: IntlShape) => {
  const options = [
    DistanceToLovedOneEnum.SAME_HOUSEHOLD,
    DistanceToLovedOneEnum.SAME_CITY,
    DistanceToLovedOneEnum.SAME_STATE,
    DistanceToLovedOneEnum.SAME_COUNTRY,
    DistanceToLovedOneEnum.DIFFERENT_COUNTRY
  ];

  return translateOptions(options, intl, 'models.careProfile.distanceToLovedOneOptions');
};

export const optionsForDurableMedicalEquipment = (intl: IntlShape) =>
  translateOptions(
    Object.values(DurableMedicalEquipmentEnum),
    intl,
    'models.careProfile.durableMedicalEquipmentOptions'
  );

export const optionsForDurationUnits = (intl: IntlShape, defaultOptions?: DurationUnitsEnum[]) => {
  const options = defaultOptions ?? [DurationUnitsEnum.MINUTES, DurationUnitsEnum.HOURS, DurationUnitsEnum.DAYS];

  return translateOptions(options, intl, 'models.automationTimingBlock.durationOptions');
};

export const optionsForEmotionalConcern = (intl: IntlShape) => {
  const options = [
    CareProfileEmotionalConcernEnum.depression,
    CareProfileEmotionalConcernEnum.anxiety,
    CareProfileEmotionalConcernEnum.isolation,
    CareProfileEmotionalConcernEnum.weight_changes,
    CareProfileEmotionalConcernEnum.sleep_problems,
    CareProfileEmotionalConcernEnum.none,
    CareProfileEmotionalConcernEnum.other
  ];
  return translateOptions(options, intl, 'models.careProfile.emotionalConcernsOptions');
};

export const optionsForEmploymentStatus = (intl: IntlShape) =>
  translateOptions(Object.values(CareProfileEmploymentStatusEnum), intl, 'models.careProfile.employmentStatusOptions');

export const optionsForFallCount = (intl: IntlShape) => {
  const options = [
    CareProfileFallCountEnum.none,
    CareProfileFallCountEnum.two_or_fewer,
    CareProfileFallCountEnum.three_or_more
  ];

  return translateOptions(options, intl, 'models.careProfile.fallCountOptions');
};

export const optionsForFinancialManager = (intl: IntlShape) => {
  const options = [
    CareProfileFinancialManagerEnum.loved_one,
    CareProfileFinancialManagerEnum.relative,
    CareProfileFinancialManagerEnum.fiduciary,
    CareProfileFinancialManagerEnum.unsure,
    CareProfileFinancialManagerEnum.other
  ];
  return translateOptions(options, intl, 'models.careProfile.financialManagerOptions');
};

export const optionsForFocusArea = (intl: IntlShape, allOption = true) => {
  const options = Object.values(FocusAreaEnum).map(option => ({
    label: intl.formatMessage({ id: `models.focusArea.${option}` }),
    value: option as string
  }));
  if (allOption) {
    options.unshift({ label: intl.formatMessage({ id: 'dictionary.all' }), value: '' });
  }
  return options;
};

export const optionsForGuardianType = (intl: IntlShape) =>
  translateOptions(Object.values(CareProfileGuardianTypeEnum), intl, 'models.careProfile.guardianTypeOptions');

export const optionsForGuidanceType = (intl: IntlShape, includeImages: boolean = false) => {
  const options = [
    { image: 'femaleDoctorAtComputer', value: CareProfileGuidanceTypeEnum.EXPERT_GUIDED },
    { image: 'manSittingOnBookPile', value: CareProfileGuidanceTypeEnum.SELF_GUIDED }
  ];

  return options.map(option => ({
    image: includeImages ? (option.image as ImageName) : undefined,
    label: intl.formatMessage({ id: `models.careProfile.guidanceTypeOptions.${option.value}` }),
    value: option.value
  }));
};

export const optionsForHealthInsuranceType = (intl: IntlShape) => {
  const options = [
    CareProfileHealthInsuranceTypeEnum.medicare,
    CareProfileHealthInsuranceTypeEnum.supplemental,
    CareProfileHealthInsuranceTypeEnum.medicaid,
    CareProfileHealthInsuranceTypeEnum.private,
    CareProfileHealthInsuranceTypeEnum.veteran,
    CareProfileHealthInsuranceTypeEnum.other,
    CareProfileHealthInsuranceTypeEnum.none
  ];
  return translateOptions(options, intl, 'models.careProfile.healthInsuranceTypesOptions');
};

export const optionsForHour = (intl: IntlShape) => {
  const options = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

  return translateOptions(options, intl, 'models.hour.hourOptions');
};

export const optionsForHouseholdIncomeLevel = (intl: IntlShape) =>
  translateOptions(Object.values(HouseholdIncomeLevelEnum), intl, 'models.careProfile.householdIncomeLevelOptions');

export const optionsForImmigrationStatus = (intl: IntlShape) => {
  const options = [
    CareProfileImmigrationStatusEnum.CITIZEN,
    CareProfileImmigrationStatusEnum.CONDITIONAL_RESIDENT,
    CareProfileImmigrationStatusEnum.NON_IMMIGRANT_OR_WORK_VISA,
    CareProfileImmigrationStatusEnum.PERMANENT_RESIDENT,
    CareProfileImmigrationStatusEnum.UNDOCUMENTED
  ];
  return translateOptions(options, intl, 'models.careProfile.immigrationStatusOptions');
};

export const optionsForIncomeSource = (intl: IntlShape) =>
  translateOptions(Object.values(CareProfileIncomeSourceEnum), intl, 'models.careProfile.incomeSourcesOptions');

export const optionsForInsuranceType = (intl: IntlShape) => {
  const options = [
    CareProfileInsuranceTypeEnum.life_insurance,
    CareProfileInsuranceTypeEnum.long_term_care_insurance,
    CareProfileInsuranceTypeEnum.other,
    CareProfileInsuranceTypeEnum.none
  ];
  return translateOptions(options, intl, 'models.careProfile.insuranceTypesOptions');
};

export const optionsForIsVeteran = (intl: IntlShape) => [
  { label: intl.formatMessage({ id: 'models.careProfile.isVeteranOptions.yes' }), value: true },
  { label: intl.formatMessage({ id: 'models.careProfile.isVeteranOptions.no' }), value: false }
];

export const optionsForJourneySearchDateRangeType = (intl: IntlShape) => {
  const options = [JourneysSearchDateRangeTypeEnum.CREATED_AT, JourneysSearchDateRangeTypeEnum.LAST_ACTIVITY_AT];
  return translateOptions(options, intl, 'models.journeysSearch.dateRangeTypes');
};

export const optionsForJourneySearchSortBy = (intl: IntlShape) => {
  const options = [
    UniversalSearchSortOptionEnum.SCORE,
    UniversalSearchSortOptionEnum.LAST_ACTIVITY_AT_DESC,
    UniversalSearchSortOptionEnum.LAST_ACTIVITY_AT_ASC,
    UniversalSearchSortOptionEnum.CREATED_AT_DESC,
    UniversalSearchSortOptionEnum.CREATED_AT_ASC
  ];
  return translateOptions(options, intl, 'models.journeysSearch.sortBy');
};

export const optionsForLegalDocumentKind = (intl: IntlShape, availableKinds?: LegalDocumentKindEnum[]) =>
  translateOptions(availableKinds ?? Object.values(LegalDocumentKindEnum), intl, 'models.legalDocument.kindOptions');

export const optionsForLegalDocumentStatus = (intl: IntlShape) =>
  Object.values(LegalDocumentStatusEnum).map(option => ({
    iconName: legalDocumentStatusIcon(option),
    label: intl.formatMessage({ id: `models.legalDocument.statusOptions.${option}` }),
    value: option
  }));

export const optionsForMaritalStatus = (intl: IntlShape) => {
  const options = [
    CareProfileMaritalStatusEnum.single,
    CareProfileMaritalStatusEnum.married,
    CareProfileMaritalStatusEnum.domestic_partner,
    CareProfileMaritalStatusEnum.divorced,
    CareProfileMaritalStatusEnum.separated,
    CareProfileMaritalStatusEnum.widowed,
    CareProfileMaritalStatusEnum.other
  ];
  return translateOptions(options, intl, 'models.careProfile.maritalStatusOptions');
};

export const optionsForMedicationKind = (intl: IntlShape) => {
  const options = [
    MedicationKindEnum.prescription,
    MedicationKindEnum.over_the_counter,
    MedicationKindEnum.vitamin_or_supplement
  ];

  return translateOptions(options, intl, 'models.medication.kindOptions');
};

export const optionsForMedicationFormat = (intl: IntlShape) => {
  const options = [
    MedicationFormatEnum.injection,
    MedicationFormatEnum.oral,
    MedicationFormatEnum.suppository,
    MedicationFormatEnum.topical,
    MedicationFormatEnum.other
  ];
  return translateOptions(options, intl, 'models.medication.formatOptions');
};

export const optionsForMedicationPaymentType = (intl: IntlShape) => {
  const options = [
    MedicationPaymentTypeEnum.covered_by_insurance,
    MedicationPaymentTypeEnum.self_pay,
    MedicationPaymentTypeEnum.unknown
  ];
  return translateOptions(options, intl, 'models.medication.paymentTypeOptions');
};

export const optionsForMedicationManagementStatus = (intl: IntlShape) =>
  translateOptions(
    Object.values(CareProfileMedicationManagementStatusEnum),
    intl,
    'models.careProfile.medicationManagementStatusOptions'
  );

export const optionsForPronouns = (intl: IntlShape) => {
  const options = [
    PronounsEnum.HE_HIM_HIS,
    PronounsEnum.SHE_HER_HERS,
    PronounsEnum.THEY_THEM_THEIRS,
    PronounsEnum.ZE_HIR_HIR,
    PronounsEnum.PER_PER_PERS,
    PronounsEnum.IT_IT_ITS,
    PronounsEnum.DO_NOT_USE_PRONOUNS,
    PronounsEnum.OTHER
  ];
  return translateOptions(options, intl, 'models.careProfile.pronounsOptions');
};

export const optionsForProviderEmotionalState = (intl: IntlShape) => {
  const options = [
    ClientRoleProviderEmotionalStateEnum.very_negative,
    ClientRoleProviderEmotionalStateEnum.negative,
    ClientRoleProviderEmotionalStateEnum.neutral,
    ClientRoleProviderEmotionalStateEnum.positive,
    ClientRoleProviderEmotionalStateEnum.very_positive
  ];
  return options.map(option => ({
    iconName: providerEmotionalStateIcon(option),
    label: intl.formatMessage({
      id: `models.careProfile.providerEmotionalStateOptions.${option}`
    }),
    value: option
  }));
};

export const optionsForProviderFeelsSupported = (intl: IntlShape) => {
  const options = [
    { value: ClientRoleProviderFeelsSupportedEnum.yes },
    { value: ClientRoleProviderFeelsSupportedEnum.no },
    { value: ClientRoleProviderFeelsSupportedEnum.its_complicated }
  ];
  return options.map(option => ({
    iconName: providerFeelsSupportedIcon(option.value),
    label: intl.formatMessage({
      id: `models.careProfile.providerFeelsSupportedOptions.${option.value}`
    }),
    value: option.value
  }));
};

export const optionsForProviderFinancialConcerns = (intl: IntlShape) => {
  const options = [
    CareProfileProviderFinancialConcernEnum.yes,
    CareProfileProviderFinancialConcernEnum.no,
    CareProfileProviderFinancialConcernEnum.NOT_ADDRESSED
  ];
  return translateOptions(options, intl, 'models.careProfile.providerFinancialConcernsOptions');
};

export const optionsForRelationship = (
  intl: IntlShape,
  translationPrefix: string,
  skipOther: boolean = false,
  includeImages: boolean = false,
  translationValues: { [key: string]: string } | undefined = undefined
) => {
  let options = [
    { image: 'huggingHeart', value: ClientRoleRelationshipEnum.myself },
    { image: 'givingFlowers', value: ClientRoleRelationshipEnum.spouse },
    { image: 'domesticPartner', value: ClientRoleRelationshipEnum.domestic_partner },
    { image: 'manSittingWithLaptop', value: ClientRoleRelationshipEnum.parent },
    { image: 'family', value: ClientRoleRelationshipEnum.parent_in_law },
    { image: 'dancingSeniors', value: ClientRoleRelationshipEnum.grandchild },
    { image: 'manWithSenior', value: ClientRoleRelationshipEnum.grandparent },
    { image: 'happyFamily', value: ClientRoleRelationshipEnum.child },
    { image: 'fosterParent', value: ClientRoleRelationshipEnum.foster_child },
    { image: 'friendOfParent', value: ClientRoleRelationshipEnum.child_of_friend_or_relative },
    { image: 'happyWomenJumping', value: ClientRoleRelationshipEnum.sibling },
    { image: 'auntOrUncle', value: ClientRoleRelationshipEnum.niece_or_nephew },
    { image: 'manHoldingLargePhone', value: ClientRoleRelationshipEnum.relative },
    { image: 'fishingInBoat', value: ClientRoleRelationshipEnum.friend },
    { image: 'womanChairComputer', value: ClientRoleRelationshipEnum.other }
  ];

  if (skipOther) {
    options = options.filter(option => option.value !== ClientRoleRelationshipEnum.other);
  }
  return options.map(option => ({
    image: includeImages ? (option.image as ImageName) : undefined,
    label: intl.formatMessage({ id: `${translationPrefix}.${option.value}` }, translationValues),
    value: option.value
  }));
};

export const optionsForReligion = (intl: IntlShape) => {
  const options = [
    CareProfileReligionEnum.none,
    CareProfileReligionEnum.atheism,
    CareProfileReligionEnum.bahai,
    CareProfileReligionEnum.buddhism,
    CareProfileReligionEnum.catholicism,
    CareProfileReligionEnum.christianity,
    CareProfileReligionEnum.hinduism,
    CareProfileReligionEnum.islam,
    CareProfileReligionEnum.jainism,
    CareProfileReligionEnum.jehovahs_witness,
    CareProfileReligionEnum.judaism,
    CareProfileReligionEnum.mormonism,
    CareProfileReligionEnum.other,
    CareProfileReligionEnum.paganism,
    CareProfileReligionEnum.rastafari,
    CareProfileReligionEnum.santeria,
    CareProfileReligionEnum.shinto,
    CareProfileReligionEnum.sikhism,
    CareProfileReligionEnum.spiritualism,
    CareProfileReligionEnum.taoism,
    CareProfileReligionEnum.unitarianism,
    CareProfileReligionEnum.zoroastrianism
  ];
  return translateOptions(options, intl, 'models.careProfile.religionOptions');
};

export const optionsForResidenceType = (intl: IntlShape) => {
  const options = [
    { option: CareProfileResidenceTypeEnum.home_alone },
    { option: CareProfileResidenceTypeEnum.home_with_family },
    { option: CareProfileResidenceTypeEnum.home_with_others },
    { option: CareProfileResidenceTypeEnum.group_home },
    { option: CareProfileResidenceTypeEnum.independent_living_community },
    { option: CareProfileResidenceTypeEnum.assisted_living_community },
    { option: CareProfileResidenceTypeEnum.memory_care_community },
    { option: CareProfileResidenceTypeEnum.skilled_nursing_facility },
    { option: CareProfileResidenceTypeEnum.foster_care },
    { option: CareProfileResidenceTypeEnum.unhoused },
    { option: CareProfileResidenceTypeEnum.other }
  ];
  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.careProfile.residenceTypeOptions.${option.option}`
    }),
    value: option.option
  }));
};

export const optionsForSenseOfFulfillment = (intl: IntlShape) => {
  const options = [
    CareProfileSenseOfFulfillmentEnum.not_at_all,
    CareProfileSenseOfFulfillmentEnum.level_2,
    CareProfileSenseOfFulfillmentEnum.neutral,
    CareProfileSenseOfFulfillmentEnum.level_4,
    CareProfileSenseOfFulfillmentEnum.extremely,
    CareProfileSenseOfFulfillmentEnum.NOT_ADDRESSED
  ];
  return options.map(option => ({
    iconName: senseOfFulfillmentIcon(option),
    label: intl.formatMessage({
      id: `models.careProfile.senseOfFulfillmentOptions.${option}`
    }),
    value: option
  }));
};

export const optionsForSocialSatisfaction = (intl: IntlShape) => {
  const options = [
    CareProfileSocialSatisfactionEnum.not_at_all,
    CareProfileSocialSatisfactionEnum.level_2,
    CareProfileSocialSatisfactionEnum.neutral,
    CareProfileSocialSatisfactionEnum.level_4,
    CareProfileSocialSatisfactionEnum.extremely,
    CareProfileSocialSatisfactionEnum.NOT_ADDRESSED
  ];

  return options.map(option => ({
    iconName: socialSatisfactionIcon(option),
    label: intl.formatMessage({
      id: `models.careProfile.socialSatisfactionOptions.${option}`
    }),
    value: option
  }));
};

export const optionsForSeverity = (intl: IntlShape) =>
  translateOptions(Object.values(SeverityEnum), intl, 'models.careProfile.severityOptions');

export const optionsForSpecialtyAge = (intl: IntlShape) => {
  const options = [
    SpecialtyAgeEnum.sixty_five_and_older,
    SpecialtyAgeEnum.eighteen_to_sixty_four,
    SpecialtyAgeEnum.under_eighteen,
    SpecialtyAgeEnum.deceased
  ];

  return options.map(option => ({
    color: '#5f49d3',
    label: intl.formatMessage({ id: `models.specialties.age.${option}` }),
    value: option
  }));
};

export const optionsForStage = (intl: IntlShape) => {
  const options = [
    StageEnum.independent,
    StageEnum.interdependent,
    StageEnum.dependent,
    StageEnum.crisis,
    StageEnum.end_of_life,
    StageEnum.bereavement
  ];
  return translateOptions(options, intl, 'models.journey.stage');
};

export const optionsForStageLimited = (intl: IntlShape) => {
  const options = [
    CareProfileStageLimitedEnum.independent,
    CareProfileStageLimitedEnum.interdependent,
    CareProfileStageLimitedEnum.dependent
  ];
  return translateOptions(options, intl, 'models.careProfile.stageLimited');
};

export const optionsForStabilitySupport = (intl: IntlShape) => {
  const options = [
    CareProfileStabilitySupportEnum.cane_or_walker,
    CareProfileStabilitySupportEnum.wheelchair,
    CareProfileStabilitySupportEnum.objects,
    CareProfileStabilitySupportEnum.none
  ];

  return translateOptions(options, intl, 'models.careProfile.stabilitySupportsOptions');
};

export const optionsForUrgency = (intl: IntlShape, includeImages: boolean = false) => {
  const options = [
    { image: 'womanAlarmClock', value: CareProfileUrgencyEnum.asap },
    { image: 'womanSittingOnGiantMugNoSteam', value: CareProfileUrgencyEnum.soon },
    { image: 'manWithGiantMagnifyingGlass', value: CareProfileUrgencyEnum.just_browsing }
  ];

  return options.map(option => ({
    image: includeImages ? (option.image as ImageName) : undefined,
    label: intl.formatMessage({ id: `models.careProfile.urgencyOptions.${option.value}` }),
    value: option.value
  }));
};

export const optionsForTaskKinds = (intl: IntlShape) => {
  const options = Object.values(TaskKindEnum);

  return translateOptions(options, intl, 'models.tasks.kind');
};

export const optionsForPushNotificationAction = (intl: IntlShape) => {
  const options = [
    PushNotificationActionEnum.OPEN_CARE_PLAN,
    PushNotificationActionEnum.OPEN_CONSULTS,
    PushNotificationActionEnum.OPEN_MESSAGES
  ];

  return translateOptions(options, intl, 'models.pushNotificationActions');
};

export const optionsForSignedDocumentKind = (intl: IntlShape) => {
  const options = [SignedDocumentKindEnum.CONSENT_FORM];

  return translateOptions(options, intl, 'models.signedDocument.kindOptions');
};

export const optionsForTaskClientVisible = (intl: IntlShape) => {
  const options = [
    TaskClientVisibleEnum.ANY_TASK,
    TaskClientVisibleEnum.CLIENT_VISIBLE,
    TaskClientVisibleEnum.EXPERT_ONLY
  ];

  return translateOptions(options, intl, 'models.tasks.taskClientVisibleOptions');
};

export const optionsForTrackerType = (intl: IntlShape) => {
  const options = [
    TrackerTypeEnum.legal_documents,
    TrackerTypeEnum.medication_tracker,
    TrackerTypeEnum.provider_tracker
  ];

  return translateOptions(options, intl, 'models.tracker.typeOptions');
};

export const optionsForTypesOfSupportNeeded = (intl: IntlShape) =>
  translateOptions(
    Object.values(CareProfileTypesOfSupportNeededEnum),
    intl,
    'models.careProfile.typesOfSupportNeededOptions'
  );

export const optionsForUseCase = (intl: IntlShape) => {
  const options = [
    CareProfileUseCaseEnum.GRIEVING,
    CareProfileUseCaseEnum.AGING_LOVED_ONE,
    CareProfileUseCaseEnum.ADULT,
    CareProfileUseCaseEnum.self,
    CareProfileUseCaseEnum.CHILD,
    CareProfileUseCaseEnum.INFANT,
    CareProfileUseCaseEnum.PREGNANCY
  ];
  return translateOptions(options, intl, 'models.careProfile.useCaseOptions');
};

export const optionsForCarePhase = (intl: IntlShape) => {
  const options = [CareProfileCarePhaseEnum.ACTIVELY_CARING, CareProfileCarePhaseEnum.PLANNING_CARE];
  return translateOptions(options, intl, 'models.careProfile.carePhaseOptions');
};

export const optionsForYesNoDontKnow = (intl: IntlShape) => {
  const options = [YesNoDontKnowEnum.yes, YesNoDontKnowEnum.no, YesNoDontKnowEnum.i_dont_know];

  return translateOptions(options, intl, 'models.careProfile.yesNoDontKnowOptions');
};

type YesNoDontKnowFormData = {
  [fieldName: string]: YesNoDontKnowEnum | null;
};

export const optionsForYesNoDontKnowForOnboarding = <FormData extends YesNoDontKnowFormData>(intl: IntlShape) =>
  [YesNoDontKnowEnum.yes, YesNoDontKnowEnum.no, YesNoDontKnowEnum.i_dont_know].map(option => {
    const choice: Choice<FormData> = {
      id: option,
      label: intl.formatMessage({ id: `models.careProfile.yesNoDontKnowOptions.${option}` }),
      value: option as PathValue<FormData, Path<FormData>>
    };
    return choice;
  });

export const optionsForYesNo = (intl: IntlShape) => {
  const options = [YesNoDontKnowEnum.yes, YesNoDontKnowEnum.no];

  return translateOptions(options, intl, 'models.careProfile.yesNoDontKnowOptions');
};

export const durationOptions = (intl: IntlShape) =>
  [ConsultDurationEnum.thirty_minutes, ConsultDurationEnum.one_hour].map(option => ({
    label: intl.formatMessage({ id: `models.duration.${option}` }),
    value: option
  }));

export const contactMethodOptions = (intl: IntlShape) =>
  [ContactMethodEnum.ZOOM, ContactMethodEnum.PHONE].map(option => ({
    label: intl.formatMessage({ id: `pages.consults.contactMethod.${option}` }),
    value: option
  }));

export const orderedCareProfileUseCases = [
  CareProfileUseCaseEnum.PREGNANCY,
  CareProfileUseCaseEnum.INFANT,
  CareProfileUseCaseEnum.CHILD,
  CareProfileUseCaseEnum.ADULT,
  CareProfileUseCaseEnum.self,
  CareProfileUseCaseEnum.AGING_LOVED_ONE,
  CareProfileUseCaseEnum.GRIEVING
];

export const optionsForCareProfileActionableFieldValue = (
  careProfileField: CareProfileActionableFieldEnum | null,
  intl: IntlShape
) => {
  if (careProfileField === CareProfileActionableFieldEnum.age) {
    return optionsForAge(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.caregiver_types) {
    return optionsForCaregiverType(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.cognitive_statuses) {
    return optionsForCognitiveStatus(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.current_care_transitions) {
    return optionsForCurrentCareTransition(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.emotional_concerns) {
    return optionsForEmotionalConcern(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.fall_count) {
    return optionsForFallCount(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.financial_manager) {
    return optionsForFinancialManager(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.guidance_type) {
    return optionsForGuidanceType(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.health_insurance_types) {
    return optionsForHealthInsuranceType(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.insurance_types) {
    return optionsForInsuranceType(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.legal_help_needed) {
    return optionsForYesNo(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.marital_status) {
    return optionsForMaritalStatus(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.medications_affordable) {
    return optionsForYesNoDontKnow(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.provider_financial_concerns) {
    return optionsForProviderFinancialConcerns(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.recent_hospitalization) {
    return optionsForYesNo(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.religion) {
    return optionsForReligion(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.residence_type) {
    return optionsForResidenceType(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.sense_of_fulfillment) {
    return optionsForSenseOfFulfillment(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.social_satisfaction) {
    return optionsForSocialSatisfaction(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.stability_supports) {
    return optionsForStabilitySupport(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.types_of_support_needed) {
    return optionsForTypesOfSupportNeeded(intl);
  } else if (careProfileField === CareProfileActionableFieldEnum.urgency) {
    return optionsForUrgency(intl);
  }

  return [];
};
